import { OccupationAPIPromiseClient } from "../../../gen/witbooking/backoffice/occupation/v1/occupation_api_grpc_web_pb";
import {
  CreateOccupationRequest,
  DeleteOccupationRequest,
  GetAllConfigurationRequest,
  GetAllOccupationsRequest,
  GetAllOccupationsResponse,
  GetOccupationRequest,
  Occupation,
  Translation,
  UpdateOccupationRequest,
} from "../../../gen/witbooking/backoffice/occupation/v1/occupation_api_pb";
import { addAuthzMetadataGrpcCall, buildGrpcClient } from "../../../lib/grpc";
import { OccupationEntity } from "../domain/occupation.entity";

export const occupationClient: OccupationAPIPromiseClient = buildGrpcClient(
  OccupationAPIPromiseClient
);

export function toOccupationEntities(response: GetAllOccupationsResponse): OccupationEntity[] {
  return response
    .getOccupationsList()
    .map(m => ({ ...m.toObject(), autoGenerateTranslations: true }));
}

function translationFromObject(obj: Translation.AsObject): Translation {
  const result = new Translation();
  result.setLocale(obj.locale);
  result.setName(obj.name);
  result.setDescription(obj.description);
  return result;
}
function occupationFromObject(obj: OccupationEntity): Occupation {
  const result = new Occupation();
  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setTranslationsList(obj.translationsList.map(translationFromObject));
  result.setSeniors(obj.seniors);
  result.setAdults(obj.adults);
  result.setJuniors(obj.juniors);
  result.setChildren(obj.children);
  result.setBabies(obj.babies);
  result.setPax(obj.pax);
  return result;
}

export function getAllOccupations(): Promise<OccupationEntity[]>;
export function getAllOccupations(ticker: string, token: string): Promise<OccupationEntity[]>;
export async function getAllOccupations(
  ticker?: string,
  token?: string
): Promise<OccupationEntity[]> {
  const request = new GetAllOccupationsRequest();

  if (ticker && token) {
    const response = await occupationClient.getAllOccupations(request, {
      "x-property": ticker,
      Authorization: `Bearer ${token}`,
    });
    return toOccupationEntities(response);
  }

  const call = addAuthzMetadataGrpcCall(occupationClient, occupationClient.getAllOccupations);
  return toOccupationEntities(await call(request));
}

export async function getOccupation(id: string): Promise<OccupationEntity> {
  const request = new GetOccupationRequest();
  request.setId(id);
  const getOccupation = addAuthzMetadataGrpcCall(occupationClient, occupationClient.getOccupation);
  const response = await getOccupation(request);
  const occupation = response.getOccupation();
  if (!occupation) {
    throw new Error("Unexpected reply obtaining occupation");
  }
  return { ...occupation?.toObject(), autoGenerateTranslations: true };
}

export async function updateOccupation(entity: OccupationEntity): Promise<void> {
  const request = new UpdateOccupationRequest();
  request.setOccupation(occupationFromObject(entity));
  const updateOccupation = addAuthzMetadataGrpcCall(
    occupationClient,
    occupationClient.updateOccupation
  );
  await updateOccupation(request);
}

export async function createOccupation(entity: OccupationEntity): Promise<string> {
  const request = new CreateOccupationRequest();
  request.setOccupation(occupationFromObject(entity));
  const createOccupation = addAuthzMetadataGrpcCall(
    occupationClient,
    occupationClient.createOccupation
  );
  const response = await createOccupation(request);
  const createdOccupation = response.getOccupation();
  if (!createdOccupation) {
    throw new Error("Failed to create occupation");
  }
  return createdOccupation.getId();
}

export async function deleteOccupation(id: string): Promise<void> {
  const request = new DeleteOccupationRequest();
  request.setId(id);
  const deleteOccupation = addAuthzMetadataGrpcCall(
    occupationClient,
    occupationClient.deleteOccupation
  );
  await deleteOccupation(request);
}

export async function getAllConfigurations(): Promise<OccupationEntity[]> {
  const request = new GetAllConfigurationRequest();
  const getAllConfigurations = addAuthzMetadataGrpcCall(
    occupationClient,
    occupationClient.getAllConfigurations
  );
  const response = await getAllConfigurations(request);
  return response
    .getConfigurationsList()
    .map(it => ({ ...it.toObject(), autoGenerateTranslations: true }));
}
